import { SourcingCandidateTableColumns } from 'modules/sourcing-shared/tables/types/SourcingCandidateTable';
import { TableV3Filters } from 'common/table/components/TableV3';
import {
  SearchableSourcedCandidateFilterInput,
  SourcedCandidateStatus
} from 'generated-graphql/types';
import {
  SourcingConversationStatusFilter,
  getConversationStatusesFromFilter
} from 'modules/sourcing-shared/constants/conversationStatusFilterOptions';
import {
  SourcingStatusFilter,
  getSourcedCandidateStatusesFromFilter
} from 'modules/sourcing-shared/constants/sourcingStatusFilterOptions';

type GetSourcingCandidateFiltersProps = {
  organizationId: string;
  candidateBatchId?: string;
  filters: TableV3Filters<SourcingCandidateTableColumns>;
};

export const getSourcingCandidateFilters = ({
  organizationId,
  candidateBatchId,
  filters
}: GetSourcingCandidateFiltersProps): SearchableSourcedCandidateFilterInput => {
  const filterObject: SearchableSourcedCandidateFilterInput = {};

  filterObject.organizationId = { eq: organizationId };

  if (candidateBatchId) {
    filterObject.candidateBatchId = { eq: candidateBatchId };
  }

  // Exclude candidates with AwaitingEnrichment status
  filterObject.status = { ne: SourcedCandidateStatus.AwaitingEnrichment };

  const searchFields = filters.searchFields;

  if (searchFields) {
    Object.entries(searchFields).forEach(([key, value]) => {
      switch (key as keyof SourcingCandidateTableColumns) {
        case 'name':
          filterObject.fullName = { matchPhrasePrefix: value };
          break;
        case 'location':
          filterObject.location = { matchPhrasePrefix: value };
          break;
        case 'phone':
          filterObject.phoneNumber = { matchPhrasePrefix: value };
          break;
        default:
          break;
      }
    });
  }

  // Apply filter values
  const filterValues = filters.filterValues;
  if (filterValues) {
    Object.entries(filterValues).forEach(([key, value]) => {
      switch (key as keyof SourcingCandidateTableColumns) {
        case 'origin':
          if (value.length > 0) {
            filterObject.and = value.map((item) => ({
              source: { eq: item.value }
            }));
          }
          break;
        case 'status':
          if (value.length > 0) {
            const statusFilters: any[] = [];

            value.forEach((item) => {
              const filterValue = item.value as SourcingStatusFilter;

              // Handle CANDIDATE_APPLIED filter separately as it checks candidateId
              if (filterValue === 'CANDIDATE_APPLIED') {
                statusFilters.push({
                  candidateId: { exists: true }
                });
              } else {
                // Get the actual SourcedCandidateStatus values for this filter
                const statuses =
                  getSourcedCandidateStatusesFromFilter(filterValue);
                if (statuses.length > 0) {
                  const statusCondition =
                    statuses.length === 1
                      ? { status: { eq: statuses[0] } }
                      : {
                          or: statuses.map((status) => ({
                            status: { eq: status }
                          }))
                        };

                  // For non-CANDIDATE_APPLIED statuses, only show candidates without candidateId
                  statusFilters.push({
                    and: [statusCondition, { candidateId: { exists: false } }]
                  });
                }
              }
            });

            if (statusFilters.length > 0) {
              // Combine with existing filters using AND
              if (filterObject.and) {
                filterObject.and = [...filterObject.and, { or: statusFilters }];
              } else {
                filterObject.and = [{ or: statusFilters }];
              }
            }
          }
          break;
        case 'conversationStatus':
          if (value.length > 0) {
            const statusFilters: any[] = [];

            value.forEach((item) => {
              const filterValue =
                item.value as SourcingConversationStatusFilter;

              // Handle REVIEW filter separately as it uses needsReview field
              if (filterValue === SourcingConversationStatusFilter.REVIEW) {
                statusFilters.push({
                  conversationNeedsReview: { eq: true }
                });
              } else {
                // Get the actual ConversationStatus values for this filter
                const statuses = getConversationStatusesFromFilter(filterValue);
                if (statuses.length > 0) {
                  if (statuses.length === 1) {
                    statusFilters.push({
                      conversationStatus: { eq: statuses[0] }
                    });
                  } else {
                    // Multiple statuses - use OR
                    statusFilters.push({
                      or: statuses.map((status) => ({
                        conversationStatus: { eq: status }
                      }))
                    });
                  }
                }
              }
            });

            if (statusFilters.length > 0) {
              // Combine with existing filters using AND
              if (filterObject.and) {
                filterObject.and = [...filterObject.and, { or: statusFilters }];
              } else {
                filterObject.and = [{ or: statusFilters }];
              }
            }
          }
          break;
      }
    });
  }

  return filterObject;
};
